import classNames from 'classnames';
import { times } from 'lodash-es';
import i18n from '@/settings/i18n';
import { ReviewSummaryT } from '@/settings/types';
import { MIN_SUMMARY_STARS_FOR_DISPLAY } from '@/components/v2/product/hooks/constants';
import { formatStoreDecimal, formatStoreNumber } from '@/lib/intl-format';

const t = i18n.init('product');

type StarsProps = { stars: number; className?: string };

const ReviewsColorMap = {
  Five: '#00B67A',
  Four: '#74cf0f',
  Three: '#fece04',
  Two: '#ff9f00',
  One: '#ff3722',
};

export const getReviewsStarColor = (stars: number) => {
  if (stars > 4) {
    return ReviewsColorMap.Five;
  }

  if (stars > 3) {
    return ReviewsColorMap.Four;
  }

  if (stars > 2) {
    return ReviewsColorMap.Three;
  }

  if (stars > 1) {
    return ReviewsColorMap.Two;
  }

  return ReviewsColorMap.One;
};

export const Stars = ({ stars, className }: StarsProps) => {
  const leftRoundedRectPath = (
    x: number,
    y: number,
    width: number,
    height: number,
    radius: number,
  ) => {
    const r = Math.min(radius, height / 2, width);
    const right = x + width;
    const bottom = y + height;

    return [
      `M ${x + r} ${y}`,
      `H ${right}`,
      `V ${bottom}`,
      `H ${x + r}`,
      `A ${r} ${r} 0 0 1 ${x} ${bottom - r}`,
      `V ${y + r}`,
      `A ${r} ${r} 0 0 1 ${x + r} ${y}`,
      'Z',
    ].join(' ');
  };

  return (
    <div className={classNames('flex items-center', className)}>
      <svg
        role="img"
        viewBox="45 0 267 46"
        xmlns="http://www.w3.org/2000/svg"
        className={classNames('inline', className)}
      >
        <title id={`starRating${stars}`} lang={i18n.getLocale()}>
          {stars} {t`out of five star rating on Trustpilot`}
        </title>
        {times(5, (n) => (
          <g transform={`translate(${n * 53},0)`} key={n}>
            <rect
              fill={stars >= n + 1 ? getReviewsStarColor(stars) : '#dcdce6'}
              x="51.24816"
              y="0"
              width="46.375587"
              height="46.330002"
              rx="8"
              ry="8"
            />
            <path
              fill={
                stars >= n + 1 || (stars > n && stars < n + 1)
                  ? getReviewsStarColor(stars)
                  : '#dcdce6'
              }
              d={leftRoundedRectPath(51.24816, 0, 23.187793, 46.330002, 8)}
            />
            <path
              d="M74.990978 31.32991L81.150908 30 84 39l-9.660206-7.202786L64.30279 39l3.895636-11.840666L58 19.841466h12.605577L74.499595 8l3.895637 11.841466H91L74.990978 31.329909z"
              fill="#FFF"
              transform="translate(0 -1)"
            ></path>
          </g>
        ))}
      </svg>
    </div>
  );
};

const ReviewsSummary = ({
  summary,
  className,
  onClick,
}: {
  summary: ReviewSummaryT | undefined;
  className?: string;
  onClick?: () => void;
}) => {
  if (!summary) {
    return null;
  }

  const summaryStars = Math.max(MIN_SUMMARY_STARS_FOR_DISPLAY, summary.stars);

  return (
    <button
      className={classNames(
        'group flex items-center gap-1 text-xs text-gray-700 focus:outline-none',
        className,
      )}
      onClick={onClick}
    >
      <Stars stars={summaryStars} className="h-3.5" />
      <div className="flex shrink-0 items-center font-bold text-gray-700">
        <span>{formatStoreDecimal(summaryStars)}</span>
      </div>
      {summary.count > 0 && (
        <span className="text-xs underline underline-offset-2">
          ({formatStoreNumber(summary.count)}{' '}
          {summary.count === 1 ? t`review` : t`reviews`})
        </span>
      )}
    </button>
  );
};

export default ReviewsSummary;
